import { postEvent } from "@/api/privateRequests/events/postEvent";
import { SimpleAdUnit } from "@/components/AdUnit";
import { GAME_ASSET_SLUG } from "@/config/game";
import * as evolutionsMixpanel from "@/events/evolutionsMixpanel";
import * as playersMixpanel from "@/events/playersMixpanel";
import { useGridColumnCount } from "@/hooks/useGridColumnCount";
import { cn } from "@/lib/utils";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { useShowAds } from "@repo/shared/redux";
import { Fragment, ReactNode } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router";

import {
  BasePath,
  MetaRatingsPlayer,
  generateNavigationUrl,
  versionUrlSlug,
} from "../../../redux/players/playersSlice";
import { PlayersCardView } from "../Players/MrDbListCardView/PlayersCardView";
import { PlayerGridCard } from "./PlayerGridCard";

// Where an evo player lives depends on the player, so callers that don't own a
// destination (players hub, evolutions hub) fall back to this.
const destinationFromPlayer = (p: MetaRatingsPlayer): BasePath =>
  p.evolutionPath?.length > 0 ? "/evolutions/players" : "/players";

type Props = {
  players: MetaRatingsPlayer[];
  show3Chem: boolean;
  playerRoleId?: number;
  /** Fixed destination, or one derived per player. */
  basePath?: BasePath | ((player: MetaRatingsPlayer) => BasePath);
  /** Runs on click in place of the default view_content tracking. */
  onSelect?: (player: MetaRatingsPlayer) => void;
  showEvoPopularity?: boolean;
  /** "grid" draws the front page's card tiles instead of rows. */
  view?: "list" | "grid";
  /** Drawn full width between the second and third rows, e.g. a campaign unit. */
  afterSecondRow?: ReactNode;
};

// Columns follow the grid's own width (needs an @container parent), so an open
// sidebar drops columns too. 2/3/4/6/8 all divide the 24-player page.
export const PLAYERS_GRID_CLASS =
  "grid w-full grid-cols-2 gap-2 @min-[536px]:grid-cols-3 @min-[736px]:grid-cols-4 @min-[1088px]:grid-cols-6 @min-[1400px]:grid-cols-8";

export const PlayerList = ({
  players,
  show3Chem,
  playerRoleId,
  basePath,
  onSelect,
  showEvoPopularity,
  view = "list",
  afterSecondRow,
}: Props) => {
  const versionAssets = useSelector(getVersionAssetsSelector);
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;

  const resolveBasePath = (p: MetaRatingsPlayer): BasePath => {
    if (typeof basePath === "function") return basePath(p);
    return basePath ?? destinationFromPlayer(p);
  };

  // view_content: branch on the navigation target. Evolution players go to
  // /evolutions/players (Evolutions section, "Players" subsection); everything
  // else is the top-level Players hub ("Overview"). new_content = NEW tag.
  const trackDefaultNavigation = (
    p: MetaRatingsPlayer,
    destination: BasePath,
  ) => {
    postEvent({ event: "detailed-player-view", resourceId: p.resourceId });
    if (destination === "/evolutions/players") {
      evolutionsMixpanel.trackViewContent({
        subsection: "Players",
        content_type: "evo_player",
        new_content: p.newPlayer,
      });
    } else {
      playersMixpanel.trackViewContent({
        subsection: "Overview",
        new_content: p.newPlayer,
      });
    }
  };

  // List: one ad every 8 rows.
  const showAds = useShowAds();
  const showAdBefore = (index: number) =>
    showAds && index % 8 === 0 && index > 0;

  // A row is one player in the list, or as many tiles as fit in the grid.
  const [gridRef, columns] = useGridColumnCount();
  const afterIndex =
    Math.min(2 * (view === "grid" ? columns : 1), players.length) - 1;

  const items = players.map((p, index) => {
    const versionSlug = versionUrlSlug(versionAssets[p.versionId]);
    const destination = resolveBasePath(p);

    const navLink = generateNavigationUrl({
      playerRoleId,
      resourceId: p.resourceId,
      name: p.name,
      versionSlug,
      evolutionPath: p.evolutionPath,
      pathwayPath: p.pathwayPath,
      evoPathId: p.evoPathId,
      canEvolve: p.canEvolve,
      basePath: destination,
    });

    return (
      <Fragment key={navLink + index}>
        {showAdBefore(index) && view === "list" && <SimpleAdUnit />}
        <NavLink
          to={navLink}
          // Sorted at 3 chem → the player view opens at 3 chem too
          // (SET_CHEMISTRY derives the best chemstyle itself).
          state={show3Chem ? { chemistryContext: 3 } : undefined}
          draggable="false"
          onClick={() =>
            onSelect ? onSelect(p) : trackDefaultNavigation(p, destination)
          }
        >
          {view === "grid" ? (
            <PlayerGridCard
              player={p}
              playerRoleId={playerRoleId}
              show3Chem={show3Chem}
            />
          ) : (
            <PlayersCardView
              assetId={p.resourceId}
              chemistry={show3Chem ? 3 : 0}
              {...p}
              desktopCardUrl={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/playercards/square/${p.resourceId}.png`}
              mobileCardUrl={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/playercards/small/${p.resourceId}.png`}
              metaRating={p.metaRating}
              playerRoleId={playerRoleId ?? p.playerRoleId}
              untradeable={p.untradeable}
              manuallyChosenPlayerRole={!!playerRoleId}
              evoPopularity={showEvoPopularity ? p.evoPopularity : undefined}
            />
          )}
        </NavLink>
        {index === afterIndex && afterSecondRow}
      </Fragment>
    );
  });

  return view === "grid" ? (
    <div className="@container w-full">
      <div ref={gridRef} className={PLAYERS_GRID_CLASS}>
        {items}
      </div>
    </div>
  ) : (
    <>{items}</>
  );
};
